import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// Display helpers live here so the events section, the hero carousel, and the
// admin list all format a given event identically.
export const EVENT_TYPES = ['networking', 'training', 'wellness', 'career', 'support'];

export const EVENT_TYPE_COLORS = {
  networking: '#4DAAAA',
  training: 'var(--brand-primary)',
  wellness: '#D4A03C',
  career: '#E07A5F',
  support: '#9B7EC8',
};

export const formatEventDay = (date) => ({
  month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
  day: date.getDate(),
});

export const formatEventTime = (date) =>
  date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

// "Sep 18" / "Sep 18, 2027" once it crosses a year boundary
export const formatEventDate = (date) => {
  const opts = { month: 'short', day: 'numeric' };
  if (date.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return date.toLocaleDateString('en-US', opts);
};

// Local timezone abbreviation ("PDT", "EST"). starts_at is timestamptz and
// there is no timezone column, so every time in the app is rendered in the
// VIEWER's zone. Naming the zone is what keeps that honest: a California
// member seeing "11:00 AM PDT" for a webinar NADSP advertises as 2:00 PM ET is
// correct, not a bug, but only if the label says which zone it is.
export const localZoneAbbrev = (date) => {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? '';
  } catch {
    return '';
  }
};

// "Sep 15 · 2:00 PM PDT" / "Sep 15 · 2:00 – 3:00 PM PDT" — the concrete when,
// for surfaces that need the actual date rather than a countdown.
export const formatEventWhen = (startsAt, endsAt) => {
  const zone = localZoneAbbrev(startsAt);
  const sameDay = endsAt && endsAt.toDateString() === startsAt.toDateString();
  const time = !endsAt
    ? formatEventTime(startsAt)
    : sameDay
    ? `${formatEventTime(startsAt)} – ${formatEventTime(endsAt)}`
    : `${formatEventTime(startsAt)} – ${formatEventDate(endsAt)}, ${formatEventTime(endsAt)}`;
  return [formatEventDate(startsAt), [time, zone].filter(Boolean).join(' ')].join(' · ');
};

// Urgency label for the hero slot — the whole reason an event earns that space.
//
// Counts CALENDAR days in the viewer's timezone, not 24-hour blocks. It used to
// floor (startsAt - now) / 24h, so anything less than a day away said "Today" —
// a 2 PM event tomorrow read "Today" all afternoon. Math.round absorbs the
// 23/25-hour days at DST changes.
const localMidnight = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const eventCountdown = (startsAt, now = new Date()) => {
  if (startsAt.getTime() <= now.getTime()) return 'Happening now';
  const days = Math.round((localMidnight(startsAt) - localMidnight(now)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 7) return `In ${days} days`;
  return formatEventDate(startsAt);
};

export const normalizeEvent = (e) => ({
  id: e.id,
  title: e.title,
  description: e.description || null,
  type: e.event_type,
  startsAt: new Date(e.starts_at),
  endsAt: e.ends_at ? new Date(e.ends_at) : null,
  isVirtual: !!e.is_virtual,
  location: e.location || null,
  registrationUrl: e.registration_url || null,
  host: e.host || null,
  imageUrl: e.image_url || null,
  featured: !!e.featured,
  active: e.active !== undefined ? !!e.active : true,
});

export function useEvents({ limit = 20 } = {}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_upcoming_events', { p_limit: limit });
    if (error) {
      console.error('get_upcoming_events:', error);
      return;
    }
    setEvents((data ?? []).map(normalizeEvent));
  }, [limit]);

  useEffect(() => {
    let cancelled = false;
    fetchEvents().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetchEvents]);

  return { events, loading, refresh: fetchEvents };
}
